import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink, 
  Loader2,
  Settings,
  ChevronRight
} from "lucide-react";

const StripeConnectCard = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    fetchStripeStatus();
    
    // Handle return from Stripe
    const stripeParam = searchParams.get("stripe");
    if (stripeParam === "success") {
      toast.success("Configuration Stripe terminée ! Vérification en cours...");
      searchParams.delete("stripe");
      setSearchParams(searchParams);
      // Refresh status after a short delay
      setTimeout(() => fetchStripeStatus(), 2000);
    } else if (stripeParam === "refresh") {
      toast.info("Veuillez terminer la configuration de votre compte Stripe");
      searchParams.delete("stripe");
      setSearchParams(searchParams);
    }
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const response = await fetch(`${API}/nurses/stripe-connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStripeStatus(data);
      }
    } catch (error) {
      console.error("Error fetching Stripe status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API}/nurses/stripe-connect/onboarding`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de la connexion à Stripe");
      }
      
      const data = await response.json();
      
      // Redirect to Stripe onboarding
      window.location.href = data.url;
      
    } catch (error) {
      toast.error(error.message);
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API}/nurses/stripe-connect/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Impossible d'accéder au tableau de bord Stripe");
      }
      
      const data = await response.json();
      window.open(data.url, "_blank");
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-[#00E073] animate-spin" />
        </div>
      </div>
    );
  }

  // Already configured and active
  if (stripeStatus?.can_receive_payments) {
    return (
      <div className="card-dark p-6 mb-6 border-emerald-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-white">Paiements activés</h3>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                Actif
              </Badge>
            </div>
            <p className="text-sm text-[#94A3B8] mb-4">
              Votre compte Stripe est configuré. Vous pouvez recevoir les paiements des patients.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                className="border-[#2A3A4D] text-[#94A3B8] hover:text-white"
                onClick={handleOpenDashboard}
                disabled={connecting}
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Settings className="w-4 h-4 mr-2" />
                )}
                Gérer mon compte Stripe
                <ExternalLink className="w-3 h-3 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Account created but onboarding not complete
  if (stripeStatus?.has_account && !stripeStatus?.can_receive_payments) {
    return (
      <div className="card-dark p-6 mb-6 border-yellow-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-yellow-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-white">Configuration en cours</h3>
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                Incomplet
              </Badge>
            </div>
            <p className="text-sm text-[#94A3B8] mb-4">
              Votre compte Stripe n'est pas complètement configuré. Terminez la vérification pour recevoir les paiements.
            </p>
            <Button
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              onClick={handleConnectStripe}
              disabled={connecting}
              data-testid="stripe-finish-onboarding-btn"
            >
              {connecting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Terminer la configuration
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No account yet
  return (
    <div className="card-dark p-6 mb-6 border-[#00E073]/30 bg-gradient-to-br from-[#1F2A38] to-[#0D1C32]">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-[#00E073]/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-6 h-6 text-[#00E073]" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-white mb-1">Configurez vos paiements</h3>
          <p className="text-sm text-[#94A3B8] mb-4">
            Pour recevoir les paiements des patients, configurez votre compte bancaire via notre partenaire sécurisé Stripe.
          </p>
          
          <div className="bg-[#0D1C32]/50 rounded-lg p-4 mb-4">
            <p className="text-xs text-[#64748B] mb-2 font-medium">Documents nécessaires :</p>
            <ul className="text-sm text-[#94A3B8] space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-[#00E073]" />
                Pièce d'identité (CNI ou passeport)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-[#00E073]" />
                RIB / IBAN de votre compte professionnel
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-[#00E073]" />
                Numéro SIRET (auto-entrepreneur)
              </li>
            </ul>
          </div>
          
          <div className="flex flex-wrap items-center gap-4">
            <Button
              className="btn-primary"
              onClick={handleConnectStripe}
              disabled={connecting}
              data-testid="stripe-connect-btn"
            >
              {connecting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Configurer mes paiements
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
            <span className="text-xs text-[#64748B] flex items-center gap-1">
              <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
              100% sécurisé via Stripe
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectCard;
